import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { Wordmark } from '../components/Clapper';
import { useGo, useGoBack, useNavigationGuard } from '../components/Nav';
import { XIcon } from '../components/icons';
import { Banner, Button, Kbd, Select, cx } from '../components/ui';
import { bytes, clock, defaultRecordingTitle } from '../lib/format';
import type { Workspace } from '../lib/types';
import { useHotkeys } from '../lib/useHotkeys';
import { orgAcceptsRecordings, subscriptionNotice } from '../queries/orgs';
import { createUploadTransport } from '../queries/videos';
import { FileUpload } from '../recorder/FileUpload';
import { RecorderSurface } from '../recorder/Recorder';
import { RecorderPreview } from '../recorder/RecorderPreview';
import { useRecorderSession, type RecorderApi } from '../recorder/useRecorder';

/**
 * /new. Still a real route, so it is linkable and the back button means
 * something, but deliberately not a page: it takes the whole viewport, drops
 * the app's nav and footer, and says what it is across the top. Being inside
 * the recorder is a mode, not a place, and it should be obvious which one you
 * are in before you start talking.
 *
 * What this file owns that the recorder does not is the way *out*. Closing,
 * Escape, the browser's back button and a reload all funnel through one
 * navigation guard, and none of them may quietly cost somebody a take.
 */

interface Destination {
  workspace: Workspace;
  orgId: string | null;
  label: string;
}

/**
 * What leaving right now would cost. The phase names are `useRecorder`'s own;
 * nothing here is invented.
 *
 *   in-progress  rolling, paused, or flushing the tail. The take exists only in
 *                this tab's memory and the upload is still open.
 *   unsaved      finished, but the upload never completed, so the blob on this
 *                page is the only copy there is.
 *   none         nothing captured yet, or captured and safely in the library.
 */
type ExitCost = 'none' | 'in-progress' | 'unsaved';

function exitCostOf(recorder: RecorderApi): ExitCost {
  const { phase } = recorder;
  if (phase === 'recording' || phase === 'paused' || phase === 'finishing') return 'in-progress';
  // A completed `result` means the video row is closed and the share link is
  // live, so the take survives the tab closing. Without one, this page is it.
  if (phase === 'preview' && recorder.result === null) {
    return recorder.previewBlob !== null || recorder.upload.capturedBytes > 0 ? 'unsaved' : 'none';
  }
  return 'none';
}

/** What the header rail says, and what assistive tech is told, per phase. */
function statusLabelOf(recorder: RecorderApi): string {
  switch (recorder.phase) {
    case 'arming':
      return 'Waiting for a source';
    case 'ready':
      return 'Ready to record';
    case 'countdown':
      return 'Starting';
    case 'recording':
      return 'Recording';
    case 'paused':
      return 'Paused';
    case 'finishing':
      return 'Finishing';
    case 'preview':
      return 'Take finished';
    case 'unsupported':
      return 'Recording unavailable';
    default:
      return 'Not recording';
  }
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab inside a surface, and hands focus back where it came from once the
 * surface goes away. The flow feels modal, so it should behave modally to a
 * keyboard too. `active` goes false while the confirmation sits on top, which
 * has a trap of its own.
 */
function useFocusTrap<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T>(null);

  // Restoring focus belongs to the surface's whole lifetime, not to whichever
  // trap happens to be armed, so it is its own effect and runs once.
  useEffect(() => {
    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => restoreTo?.focus();
  }, []);

  useEffect(() => {
    const container = ref.current;
    if (!active || !container) return;

    if (!container.contains(document.activeElement)) {
      const first = container.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? container).focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [active]);

  return ref;
}

/**
 * The one thing standing between a running take and an accidental exit. Rendered
 * outside the flow so focus can move cleanly into it while the flow behind goes
 * inert.
 */
function ConfirmExit({
  cost,
  onDiscard,
  onStay,
}: {
  cost: Exclude<ExitCost, 'none'>;
  onDiscard: () => void;
  onStay: () => void;
}) {
  const ref = useFocusTrap<HTMLDivElement>(true);
  const titleId = useId();
  const bodyId = useId();

  // Escape here means the safe answer, which is always "stay".
  useHotkeys({ escape: onStay }, true);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 px-4 backdrop-blur-sm">
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className="card w-full max-w-md animate-fade-up p-5 shadow-raised"
      >
        <h2 id={titleId} className="text-h4 text-chalk">
          {cost === 'in-progress'
            ? 'You are still recording. Discard this take?'
            : 'This take has not been saved. Discard it?'}
        </h2>
        <p id={bodyId} className="mt-2 text-body text-chalk-dim">
          {cost === 'in-progress'
            ? 'Leaving now throws away everything captured so far. Stay here and press Finish instead, and the take is saved and shareable in a couple of seconds.'
            : 'The upload never finished, so this page holds the only copy. Stay here to retry the upload or download the file first.'}
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="primary" onClick={onStay}>
            {cost === 'in-progress' ? 'Keep recording' : 'Stay here'}
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            Discard and close
          </Button>
        </div>
      </div>
    </div>
  );
}

export function NewPage() {
  const { user } = useAuth();
  const { orgs, activeOrg } = useWorkspace();
  const go = useGo();
  const goBack = useGoBack();
  const titleId = useId();

  const destinations = useMemo<Destination[]>(() => {
    const list: Destination[] = [
      { workspace: 'personal', orgId: null, label: 'Personal library (only you)' },
    ];
    for (const org of orgs) {
      list.push({
        workspace: 'team',
        orgId: org.id,
        label: `${org.name} (the team workspace)`,
      });
    }
    return list;
  }, [orgs]);

  const [destinationKey, setDestinationKey] = useState(() =>
    activeOrg ? `team:${activeOrg.id}` : 'personal:',
  );

  const destination =
    destinations.find((item) => `${item.workspace}:${item.orgId ?? ''}` === destinationKey) ??
    destinations[0];

  const destinationOrg = destination.orgId
    ? (orgs.find((org) => org.id === destination.orgId) ?? null)
    : null;

  const notice = destinationOrg
    ? subscriptionNotice(destinationOrg.subscriptionStatus, destinationOrg.trialEndsAt)
    : null;

  const blocked = destinationOrg ? !orgAcceptsRecordings(destinationOrg.subscriptionStatus) : false;

  const transport = useMemo(() => createUploadTransport(), []);
  const makeTitle = () => defaultRecordingTitle(displayNameOf(user));

  // The session lives here rather than inside <RecorderSurface> so the exit
  // guard below can read the phase. It is deliberately not keyed on the
  // destination: changing where a recording lands must never tear down a screen
  // share the user has already granted.
  const session = useRecorderSession({
    transport,
    makeTitle,
    workspace: destination.workspace,
    orgId: destination.orgId,
  });
  const { recorder } = session;
  const { phase } = recorder;

  const cost = exitCostOf(recorder);
  const guard = useNavigationGuard(cost !== 'none');
  const trapRef = useFocusTrap<HTMLDivElement>(!guard.blocked);

  const live = phase === 'recording' || phase === 'paused';
  const uploadPercent =
    recorder.upload.capturedBytes > 0
      ? Math.min(
          100,
          Math.round((recorder.upload.uploadedBytes / recorder.upload.capturedBytes) * 100),
        )
      : null;

  /**
   * Where a recording lands is settled the moment it starts: the upload session
   * was opened with that workspace on it. Offering the choice afterwards would
   * be offering something that does nothing.
   */
  const destinationLocked = phase !== 'idle' && phase !== 'arming' && phase !== 'ready';

  /**
   * Every way out goes through here. With nothing to lose it simply leaves;
   * with a take on the line the guard turns the navigation into a question, so
   * the close button, Escape, an in-app link and the browser's own back button
   * all behave identically.
   */
  const requestExit = () => {
    if (cost === 'none') recorder.releaseSources();
    goBack('/');
  };

  const discardAndLeave = () => {
    // Order matters. Abort the upload first so half a video is not left open on
    // the server, then let the screen share and the microphone go. Stopping the
    // MediaRecorder itself is `useRecorder`'s unmount cleanup, which runs as
    // soon as the route changes.
    void recorder.discard();
    recorder.releaseSources();
    guard.proceed();
  };

  /**
   * Escape is a normal way out, except where it is far too easy to hit while
   * reaching for something else: mid-take it does nothing at all, and during
   * the countdown it already means "start now".
   */
  const escapeExits =
    phase !== 'recording' &&
    phase !== 'paused' &&
    phase !== 'countdown' &&
    phase !== 'finishing';

  useHotkeys({ escape: requestExit }, escapeExits && !guard.blocked);

  /**
   * Closing or reloading the tab is not a navigation any router can hold up, so
   * it gets a guard of its own. `useRecorder` already does this while a take is
   * rolling; this extends the same promise to a finished take whose upload
   * never completed, and keeps it independent of the router's own wiring.
   */
  useEffect(() => {
    if (cost === 'none') return;
    const guardUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guardUnload);
    return () => window.removeEventListener('beforeunload', guardUnload);
  }, [cost]);

  // The reason to ask can disappear on its own: "stop sharing" in the browser's
  // capture bar finishes the take, and the upload may complete a moment later.
  // Holding somebody at a question that no longer has a point would strand the
  // navigation they asked for.
  useEffect(() => {
    if (guard.blocked && cost === 'none') guard.proceed();
  }, [cost, guard]);

  return (
    <>
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        inert={guard.blocked}
        // `animate-fade-up` is a stylesheet animation, so prefers-reduced-motion
        // has already flattened it for anybody who asked.
        className="fixed inset-0 z-50 flex animate-fade-up flex-col bg-slate-950"
      >
        <header className="relative shrink-0 border-b border-hairline">
          <div
            className="clapper-stripes absolute inset-x-0 top-0 h-1.5 opacity-[0.14]"
            aria-hidden="true"
          />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 pb-3 pt-5 sm:px-6">
            <div className="flex items-center gap-3">
              <Wordmark />
              <span className="hidden h-6 w-px bg-hairline sm:block" aria-hidden="true" />
              <h1 id={titleId} className="text-h4 text-chalk">
                New recording
              </h1>
            </div>

            <div className="flex items-center gap-2.5">
              <span
                className={cx(
                  'h-2.5 w-2.5 shrink-0 rounded-full',
                  phase === 'recording'
                    ? 'animate-record-pulse bg-record'
                    : phase === 'paused'
                      ? 'bg-chalk-faint'
                      : 'bg-slate-600',
                )}
                aria-hidden="true"
              />
              {/* The state is announced in words rather than left to a red dot.
                  The clock sits outside the live region on purpose: five
                  announcements a second is not information. */}
              <span
                role="status"
                aria-live="polite"
                className="text-caption uppercase tracking-[0.08em] text-chalk-dim"
              >
                {statusLabelOf(recorder)}
              </span>
              {live ? (
                <span className="slate-type text-body text-chalk" data-timecode>
                  {clock(recorder.elapsedMs / 1000)}
                </span>
              ) : null}
              {live && uploadPercent !== null ? (
                <span className="tnum hidden text-caption text-chalk-faint sm:inline">
                  {recorder.upload.error
                    ? 'upload retrying'
                    : `uploaded ${uploadPercent}% of ${bytes(recorder.upload.capturedBytes)}`}
                </span>
              ) : null}
            </div>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2">
                <span className="hidden text-caption text-chalk-faint sm:inline">Saving to</span>
                {destinations.length > 1 ? (
                  <Select
                    aria-label="Where this recording is saved"
                    value={destinationKey}
                    disabled={destinationLocked}
                    title={
                      destinationLocked
                        ? 'Where a recording lands is settled when it starts'
                        : undefined
                    }
                    onChange={(event) => setDestinationKey(event.target.value)}
                    className="max-w-[13rem]"
                  >
                    {destinations.map((item) => (
                      <option
                        key={`${item.workspace}:${item.orgId ?? ''}`}
                        value={`${item.workspace}:${item.orgId ?? ''}`}
                      >
                        {item.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className="text-caption text-chalk">{destination.label}</span>
                )}
              </div>

              <Button
                variant="secondary"
                onClick={requestExit}
                aria-label="Close the recording flow"
                title="Close the recording flow"
                className="gap-1.5"
              >
                <XIcon size={15} />
                Close
              </Button>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
            {notice ? (
              <Banner tone={notice.tone} title={notice.title}>
                {notice.body}
              </Banner>
            ) : null}

            {blocked ? (
              <Banner tone="danger" title="New recordings to this workspace are paused">
                Choose your personal library instead (that one is free and always available), or
                ask an owner to sort the subscription out.
              </Banner>
            ) : null}

            <RecorderSurface
              session={session}
              suspendHotkeys={guard.blocked}
              onCancel={requestExit}
              renderUnsupported={() => (
                <div className="space-y-3">
                  <p className="text-body text-chalk-dim">
                    You can still upload a video you already have: a screen recording from your
                    phone, or an export from another tool.
                  </p>
                  <FileUpload
                    transport={transport}
                    makeTitle={(file) => file.name.replace(/\.[^.]+$/, '') || makeTitle()}
                    workspace={destination.workspace}
                    orgId={destination.orgId}
                    onUploaded={(result) => {
                      // Nothing of the recorder's is at stake on this path, and
                      // the file is already in the library.
                      recorder.releaseSources();
                      go('/v/$shareId', {
                        params: { shareId: result.shareId },
                        ignoreBlocker: true,
                      });
                    }}
                  />
                </div>
              )}
              renderPreview={(api) => (
                <RecorderPreview
                  recorder={api}
                  defaultTitle={makeTitle()}
                  // The preview's own Discard has already thrown the take away
                  // and asked its own question, so this exit does not ask again.
                  onDone={() => {
                    api.releaseSources();
                    goBack('/', { force: true });
                  }}
                  onRecordAnother={() => api.reset()}
                />
              )}
            />
          </div>
        </div>

        <footer className="shrink-0 border-t border-hairline px-4 py-2.5 sm:px-6">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-chalk-faint">
            <span>You are in the recording flow. The rest of Slate is out of the way.</span>
            <span className="flex flex-wrap items-center gap-1.5">
              {escapeExits ? (
                <>
                  <Kbd>Esc</Kbd> closes it.
                </>
              ) : (
                <>
                  <Kbd>Esc</Kbd> is ignored while a take is running, so it cannot end one by
                  accident. Use Close.
                </>
              )}
            </span>
          </p>
        </footer>
      </div>

      {guard.blocked && cost !== 'none' ? (
        <ConfirmExit cost={cost} onDiscard={discardAndLeave} onStay={guard.reset} />
      ) : null}
    </>
  );
}
